// Guest ordering (spec 2026-10-22): the signed-in employee places one order per named guest.
// Mirrors the order service rules in GuestOrderingService.

const NAME_MAX = 60;
// Latin-1 letters only, matching the order service: receipts and kitchen tickets are stored latin1.
const NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ .'-]*$/;

export const normalizeGuestName = value =>
  typeof value === 'string'
    ? (value.normalize ? value.normalize('NFC') : value)
        .replace(/[\u2018\u2019]/g, "'")
        .trim()
        .replace(/\s+/g, ' ')
    : '';

export const isValidGuestName = value => {
  const name = normalizeGuestName(value);
  return name.length > 0 && name.length <= NAME_MAX && NAME_PATTERN.test(name);
};

export const createCheckoutRequestKey = () => {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const guestFieldsForOrder = ({
  guestOrder,
  guestFirstName,
  guestLastName,
  checkoutRequestKey,
}) => {
  if (!guestOrder) return {};
  return {
    guestOrder: true,
    guestFirstName: normalizeGuestName(guestFirstName),
    guestLastName: normalizeGuestName(guestLastName),
    checkoutRequestKey,
  };
};

export const guestLabel = order =>
  order && order.guestOrder
    ? `For ${normalizeGuestName(order.guestFirstName)} ${normalizeGuestName(order.guestLastName)}`.trim()
    : null;

export const GUEST_NAME_MAX = NAME_MAX;

// ---------------------------------------------------------------------------
// "Who's this for?" (guest-ordering-who-is-this-for-ux-2026-10-23.md).
// Each basket item may carry a local guestId (never sent to the server). Guests live
// with the basket as {id, firstName, lastName, requestKey}. At checkout the basket
// is split into one order per person: the employee first, then each guest in the
// order they were added.

export const MAX_GUESTS_PER_CHECKOUT = 10;

// The policy only counts when the server says guest ordering is on.
export const isGuestPolicyEnabled = policy => !!policy && policy.enabled === true;

const hasCap = policy =>
  !!policy && policy.remainingToday !== null && policy.remainingToday !== undefined;

// Most guests one basket can take: what's left today, never more than 10.
export const maxGuestsThisCheckout = policy => {
  if (!hasCap(policy)) return MAX_GUESTS_PER_CHECKOUT;
  return Math.max(0, Math.min(policy.remainingToday, MAX_GUESTS_PER_CHECKOUT));
};

export const canAddGuest = (policy, guestCount) =>
  guestCount < maxGuestsThisCheckout(policy);

// "8 guest orders left today", "1 guest order left today", "No guest orders left today";
// null when there is no cap.
export const remainingText = policy => {
  if (!hasCap(policy)) return null;
  const left = policy.remainingToday;
  if (left <= 0) return 'No guest orders left today';
  if (left === 1) return '1 guest order left today';
  return `${left} guest orders left today`;
};

const NOTE =
  'Each person gets their own order. Their name goes on the label; you get the confirmations.';

// The note under the rows of the sheet.
export const recipientNote = (policy, guestCount) => {
  if (!canAddGuest(policy, guestCount)) {
    if (guestCount === 0) {
      return remainingText(policy) || "You can't order for more guests today.";
    }
    return "That's as many guests as you can order for today.";
  }
  const left = remainingText(policy);
  return left ? `${left}. ${NOTE}` : NOTE;
};

export const GUEST_NAME_ERROR =
  "Enter your guest's first and last name. Use letters (accents are fine), spaces, full stops, apostrophes and hyphens.";

// The sheet's subtitle: the item name, or "Repeat order from <restaurant>".
export const recipientSubtitle = payload => {
  if (!payload) return '';
  if (Array.isArray(payload.product)) {
    const name = payload.business && payload.business.name;
    return name ? `Repeat order from ${name}` : 'Repeat order';
  }
  return (payload.product && payload.product.name) || '';
};

export const findGuest = (guests, id) =>
  id ? (guests || []).find(guest => guest.id === id) || null : null;

// Whoever was picked last while they're still in the basket; otherwise Me (null).
export const defaultRecipient = (lastRecipientId, guests) =>
  findGuest(guests, lastRecipientId) ? lastRecipientId : null;

let guestSequence = 0;
export const createGuest = (firstName, lastName) => {
  guestSequence += 1;
  return {
    id: `guest-${Date.now().toString(36)}-${guestSequence}`,
    firstName: normalizeGuestName(firstName),
    lastName: normalizeGuestName(lastName),
    requestKey: createCheckoutRequestKey(),
  };
};

// "Mary Byrne", as sent and printed.
export const guestDisplayName = guest =>
  guest ? `${normalizeGuestName(guest.firstName)} ${normalizeGuestName(guest.lastName)}`.trim() : '';

const itemGuestId = product => (product && product.guestId) || null;

// Who the basket's orders are for, in display and sending order: the employee first
// (when they have items), then each guest with items, in the order they were added.
// null stands for the employee.
export const basketParties = (products, guests) => {
  const ids = (products || []).map(itemGuestId);
  const parties = [];
  if (ids.some(id => id === null)) parties.push(null);
  (guests || []).forEach(guest => {
    if (ids.indexOf(guest.id) !== -1) parties.push(guest.id);
  });
  return parties;
};

// The basket split into one group per person, each with its items and subtotal.
export const splitBasket = (products, guests) =>
  basketParties(products, guests).map(guestId => {
    const items = (products || []).filter(product => itemGuestId(product) === guestId);
    return {
      guestId,
      guest: findGuest(guests, guestId),
      products: items,
      subtotal: items.reduce((sum, product) => sum + product.price, 0),
    };
  });

// Only guests who still have items stay in the basket.
export const pruneGuests = (products, guests) => {
  const ids = (products || []).map(itemGuestId);
  return (guests || []).filter(guest => ids.indexOf(guest.id) !== -1);
};

// Basket header: "You", or the guest's name (with a "Guest" tag beside it).
export const partyHeaderName = party =>
  party && party.guest ? guestDisplayName(party.guest) : 'You';

// The cart Total: "€9.50" for one order, "3 orders · €28.50" for one order per person.
export const cartTotalText = (total, orders, currencySymbol) => {
  const amount = `${currencySymbol}${Number(total || 0).toFixed(2)}`;
  return orders > 1 ? `${orders} orders · ${amount}` : amount;
};

// The guest fields for one person's order: nothing for the employee.
export const guestFieldsForGuest = guest =>
  guest
    ? guestFieldsForOrder({
        guestOrder: true,
        guestFirstName: guest.firstName,
        guestLastName: guest.lastName,
        checkoutRequestKey: guest.requestKey,
      })
    : {};

export const NEW_CARD_SEVERAL_PEOPLE =
  'Each person gets their own order, so a new card would mean paying several times. Choose On Account or a saved card, or order for one person at a time.';
export const ON_ACCOUNT_NOT_ALLOWED =
  'Your company does not allow guest orders On Account. Please choose another payment method.';

// Checks made before anything is sent. Returns the message to show, or null.
// A new card, iDEAL and Bancontact all pay on a separate page for one order, so
// none of them can pay for several people's orders.
export const checkoutRefusal = ({ partyCount, hasGuest, payType, payWithAnotherCard, policy }) => {
  if (hasGuest && payType === 'A' && policy && policy.onAccountAllowed === false) {
    return ON_ACCOUNT_NOT_ALLOWED;
  }
  const paysOnNewPage =
    (payType === 'C' && !!payWithAnotherCard) || payType === 'I' || payType === 'B';
  if (partyCount > 1 && paysOnNewPage) {
    return NEW_CARD_SEVERAL_PEOPLE;
  }
  return null;
};

// What happened when the basket was sent as one order per person.
// placed: [{orderNumber, name}], failedName, failureMessage, notSent: [name].
export const batchAllPlaced = result => !result.failedName && result.notSent.length === 0;

export const batchResultTitle = result => {
  if (batchAllPlaced(result)) {
    return result.placed.length === 1 ? '1 order placed' : `${result.placed.length} orders placed`;
  }
  return result.placed.length === 0 ? 'Orders not placed' : "Some orders weren't placed";
};

export const batchResultLines = result => {
  const lines = [];
  if (result.placed.length > 0) {
    lines.push(
      `Placed: ${result.placed.map(item => `order ${item.orderNumber} for ${item.name}`).join(', ')}.`
    );
  }
  if (result.failedName) {
    const reason = String(result.failureMessage || "we couldn't place it").trim().replace(/\.+$/, '');
    lines.push(`Not placed for ${result.failedName}: ${reason}.`);
  }
  if (result.notSent.length > 0) {
    lines.push(`Not sent yet: ${result.notSent.join(', ')}.`);
  }
  if (!batchAllPlaced(result)) {
    lines.push(
      result.failureCode === 'PAYMENT-UNKNOWN-STATE' || result.failureCode === 'PAYMENT-DECLINED'
        ? "What wasn't ordered is still in your basket. Check My Orders or contact support before paying for that order again."
        : "What wasn't ordered is still in your basket. Tap Place Order to try again."
    );
  }
  return lines;
};

export const GUEST_NAMES_NEEDED =
  "Every guest needs a first and last name. Use letters (accents are fine), spaces, full stops, apostrophes and hyphens.";

export const isValidGuest = guest =>
  !!guest && isValidGuestName(guest.firstName) && isValidGuestName(guest.lastName);

// Place Order checks every guest before anything is sent: one bad name stops the lot.
export const guestNamesRefusal = guests =>
  (guests || []).every(isValidGuest) ? null : GUEST_NAMES_NEEDED;

// Delivery Tracking: "Order 9002 • For Mary Byrne • 23/10 11:02".
export const trackingHeading = order =>
  [`Order ${order.idOrder}`, guestLabel(order), order.dateRegOrderFormatted]
    .filter(part => part !== null && part !== undefined && part !== '')
    .join(' • ');



// WEBPACK FOOTER //
// ./src/config/guestOrdering.js